import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { inrFull, ymd } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PieChart, AlertTriangle, TrendingUp, TrendingDown, Scissors, Home, Stethoscope, Shapes, Receipt } from "lucide-react";

const pretty = (c) => (c || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
const monthStart = () => { const d = new Date(); return ymd(new Date(d.getFullYear(), d.getMonth(), 1)); };
const pct = (m) => (m === null || m === undefined ? "—" : `${m}%`);
const pl = (v) => (v < 0 ? `Loss ${inrFull(-v)}` : inrFull(v));
const ICON = { grooming: Scissors, boarding: Home, vet: Stethoscope, other: Shapes };
const TINT = { grooming: "bg-sky-50 text-sky-700", boarding: "bg-amber-50 text-amber-700", vet: "bg-violet-50 text-violet-700", other: "bg-slate-100 text-slate-600" };

function Row({ label, value, strong, tid }) {
  return (
    <div className={`flex justify-between gap-3 py-1 text-sm ${strong ? "font-bold text-slate-900" : "text-slate-600"}`}>
      <span className="min-w-0">{label}</span><span data-testid={tid} className="shrink-0 tabular-nums">{value}</span>
    </div>
  );
}

function Section({ title, rows, tid }) {
  return (
    <div data-testid={tid}>
      <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">{title}</p>
      {rows.length ? rows.map(([a, b], i) => <Row key={i} label={a} value={b} />) : <p className="text-xs text-slate-400">None in this period</p>}
    </div>
  );
}

export function HeadCard({ h, onOpen, mode, tid }) {
  const Icon = ICON[h.key] || Shapes;
  const loss = h.operating_profit < 0;
  return (
    <button onClick={onOpen} data-testid={tid} className="group rounded-2xl border border-slate-200 bg-white p-4 text-left transition-[box-shadow,transform,border-color] hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
      <div className="flex items-center gap-2"><span className={`grid h-8 w-8 place-items-center rounded-xl ${TINT[h.key]}`}><Icon className="h-4 w-4" /></span><span className="text-sm font-bold text-slate-800">{h.label}</span></div>
      {mode === "expense" ? (
        <><p className="mt-3 text-[11px] uppercase tracking-wide text-slate-400">Expenses</p><p className="font-display text-xl font-extrabold tabular-nums text-slate-900" data-testid={`${tid}-expenses`}>{inrFull(h.direct_expenses)}</p></>
      ) : (
        <>
          <p className="mt-3 text-[11px] uppercase tracking-wide text-slate-400">Revenue</p>
          <p className="font-display text-xl font-extrabold tabular-nums text-slate-900" data-testid={`${tid}-revenue`}>{inrFull(h.revenue)}</p>
          <p className={`mt-1 inline-flex items-center gap-1 text-sm font-bold tabular-nums ${loss ? "text-rose-600" : "text-emerald-700"}`} data-testid={`${tid}-profit`}>
            {loss ? <TrendingDown className="h-3.5 w-3.5" /> : <TrendingUp className="h-3.5 w-3.5" />}{loss ? "Loss " : "Profit "}{inrFull(Math.abs(h.operating_profit))}</p>
        </>)}
    </button>
  );
}

function HeadDetail({ head, range, mode, onClose }) {
  const q = useQuery({ queryKey: ["service-pnl-head", head, range], enabled: !!head,
    queryFn: async () => (await api.get(`/service-pnl/heads/${head}`, { params: range })).data });
  const d = q.data;
  const h = d?.head;
  const exp = (e) => [`${e.date} · ${pretty(e.service)} · ${pretty(e.category)}${e.subcategory ? ` · ${e.subcategory}` : ""}${e.vendor ? ` · ${e.vendor}` : ""}`, inrFull(e.amount)];
  return (
    <Dialog open={!!head} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white" data-testid={`head-detail-${mode}`}>
        <DialogHeader><DialogTitle>{h ? `${h.label} ${mode === "expense" ? "expenses" : "profitability"}` : "Loading…"}</DialogTitle></DialogHeader>
        {!d ? <p className="text-sm text-slate-400">Loading…</p> : mode === "expense" ? (
          <div className="space-y-4">
            <Row label="Total direct expenses" value={inrFull(h.direct_expenses)} strong tid="hd-exp-total" />
            <Section title="By service & expense category" rows={d.cost_rows.flatMap((c) => c.by_expense_category.map((x) => [`${pretty(c.category)} · ${pretty(x.category)}`, inrFull(x.amount)]))} tid="hd-exp-bycat" />
            <Section title="Expense records" rows={d.expenses.map(exp)} tid="hd-exp-lines" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 p-3">
              <Row label="Revenue (excl. GST, net of discounts, refunds & credit notes)" value={inrFull(h.revenue)} tid="hd-revenue" />
              <Row label="Direct expenses" value={inrFull(h.direct_expenses)} tid="hd-direct" />
              <Row label="Allocated staff salaries" value={inrFull(h.allocated_salary)} tid="hd-salary" />
              <Row label="Shared overhead" value="Not allocated" tid="hd-shared" />
              <Row label="Total costs" value={inrFull(h.total_costs)} strong tid="hd-costs" />
              <Row label="Operating profit / loss" value={pl(h.operating_profit)} strong tid="hd-profit" />
              <Row label="Margin" value={pct(h.margin_pct)} tid="hd-margin" />
            </div>
            <Section title="Revenue by service" rows={d.revenue_lines.map((l) => [l.name, inrFull(l.revenue)])} tid="hd-rev-lines" />
            <Section title="Costs by service" rows={d.cost_rows.map((c) => [`${pretty(c.category)} · expenses ${inrFull(c.direct_expenses)} · salaries ${inrFull(c.allocated_salary)}`, inrFull(c.direct_expenses + c.allocated_salary)])} tid="hd-cost-lines" />
            <Section title="Allocated staff salaries" rows={d.staff.map((s) => [`${s.name} · ${s.pct}% of ${inrFull(s.period_salary)} · ${pretty(s.service)}${s.source === "assigned_services" ? " (from assigned services)" : ""}`, inrFull(s.amount)])} tid="hd-staff-lines" />
            <Section title="Direct expense records" rows={d.expenses.map(exp)} tid="hd-exp-lines" />
          </div>)}
      </DialogContent>
    </Dialog>
  );
}

function Warnings({ d }) {
  const u = d.unallocated;
  const items = [];
  if (u.staff_without_allocation.length) items.push(<div key="sa" data-testid="spw-salary"><b>{inrFull(u.unallocated_salary)}</b> of salary for {u.staff_without_allocation.length} staff has no service allocation and is not in any card's profit: {u.staff_without_allocation.slice(0, 6).map((s) => s.name).join(", ")}{u.staff_without_allocation.length > 6 ? "…" : ""}. <Link to="/staff" className="font-semibold underline">Set allocations in Staff</Link>.</div>);
  if (d.staff_excluded?.count) items.push(<div key="ex" data-testid="spw-excluded">{d.staff_excluded.count} staff excluded — employment dates incomplete ({d.staff_excluded.items.slice(0, 3).map((s) => `${s.name}: ${s.reason}`).join("; ")}). Costs are understated.</div>);
  if (d.payroll.needs_review > 0) items.push(<div key="pr" data-testid="spw-payroll">{inrFull(d.payroll.needs_review)} of salary expenses need review (not linked to a salaried staff member).</div>);
  if (!items.length) return null;
  return (
    <div className="mt-3 space-y-1 rounded-xl border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-900" data-testid="service-pnl-warnings">
      <p className="flex items-center gap-1.5 font-bold"><AlertTriangle className="h-3.5 w-3.5" /> Missing cost information</p>{items}
    </div>
  );
}

function Range({ range, setRange, prefix }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="h-8 w-36" data-testid={`${prefix}-start`} />
      <span>to</span>
      <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="h-8 w-36" data-testid={`${prefix}-end`} />
    </div>
  );
}

export function ServiceProfitabilityCard() {
  const [range, setRange] = useState({ start: monthStart(), end: ymd() });
  const [open, setOpen] = useState(null);
  const q = useQuery({ queryKey: ["service-pnl", range], queryFn: async () => (await api.get("/service-pnl", { params: range })).data });
  const d = q.data;
  return (
    <div className="rounded-2xl border border-pawkt-border bg-white p-5 shadow-card" data-testid="service-pnl-card">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-2"><PieChart className="h-5 w-5 text-primary" /><h3 className="font-display text-lg font-extrabold text-pawkt-text">Service Profitability</h3></div>
        <Range range={range} setRange={setRange} prefix="service-pnl" />
      </div>
      {!d ? <p className="mt-4 text-sm text-slate-400">Loading…</p> : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="service-pnl-heads">
            {d.heads.map((h) => <HeadCard key={h.key} h={h} onOpen={() => setOpen(h.key)} tid={`pnl-head-${h.key}`} />)}
          </div>
          <p className="mt-2 text-[11px] text-slate-400" data-testid="service-pnl-footnote">Profit is after direct expenses and allocated staff salaries, before shared overhead ({inrFull(d.unallocated.shared_overhead)}) and other unallocated costs. Business operating result: <b data-testid="spt-business-result">{pl(d.totals.business_operating_result)}</b>.</p>
          <Warnings d={d} />
        </>)}
      <HeadDetail head={open} range={range} mode="profit" onClose={() => setOpen(null)} />
    </div>
  );
}

// Operating expenses by the same four heads; shared / unallocated / salary payments kept separate.
export function ExpenseHeads({ range }) {
  const [open, setOpen] = useState(null);
  const [extra, setExtra] = useState(false);
  const q = useQuery({ queryKey: ["service-pnl", range], enabled: !!range.start, queryFn: async () => (await api.get("/service-pnl", { params: range })).data });
  const d = q.data;
  const det = useQuery({ queryKey: ["service-pnl-head", "other", range], enabled: extra, queryFn: async () => (await api.get("/service-pnl/heads/other", { params: range })).data });
  if (q.isError) return <p className="mt-3 text-sm text-slate-400" data-testid="mi-expenses-noaccess">Expense breakdown needs the expenses permission.</p>;
  if (!d) return <p className="mt-3 text-sm text-slate-400">Loading…</p>;
  const r = d.expense_reconciliation;
  const sep = (t) => (det.data?.shared?.[t] || []).map((e) => [`${e.date} · ${pretty(e.category)}${e.vendor ? ` · ${e.vendor}` : ""}${e.reason ? ` · ${e.reason}` : ""}`, inrFull(e.amount)]);
  return (
    <div data-testid="mi-expense-heads">
      <div className="mt-2 flex items-end gap-2"><span className="font-display text-2xl font-extrabold text-pawkt-text" data-testid="mi-exp-recorded">{inrFull(r.recorded_total)}</span><span className="pb-1 text-xs text-slate-400">recorded expenses</span></div>
      <div className="mt-3 grid grid-cols-2 gap-2">{d.heads.map((h) => <HeadCard key={h.key} h={h} mode="expense" onOpen={() => setOpen(h.key)} tid={`exp-head-${h.key}`} />)}</div>
      <button onClick={() => setExtra(true)} className="mt-2 w-full rounded-xl border border-dashed border-slate-300 p-2.5 text-left text-xs text-slate-600 hover:bg-slate-50" data-testid="exp-separate-btn">
        <span className="flex items-center gap-1.5 font-bold text-slate-700"><Receipt className="h-3.5 w-3.5" /> Not assigned to a department</span>
        <span className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 sm:grid-cols-4">
          <span>Shared overhead <b data-testid="exp-shared">{inrFull(r.shared_overhead)}</b></span><span>Unallocated <b data-testid="exp-unallocated">{inrFull(r.unallocated)}</b></span>
          <span>Salary payments <b data-testid="exp-salary-payments">{inrFull(r.salary_payments)}</b></span><span>Capital <b data-testid="exp-capex">{inrFull(r.capital_expenditure)}</b></span>
        </span>
      </button>
      <p className="mt-1 text-[10px] text-slate-400">{r.note}</p>
      <HeadDetail head={open} range={range} mode="expense" onClose={() => setOpen(null)} />
      <Dialog open={extra} onOpenChange={setExtra}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white" data-testid="exp-separate-detail">
          <DialogHeader><DialogTitle>Expenses not assigned to a department</DialogTitle></DialogHeader>
          {!det.data ? <p className="text-sm text-slate-400">Loading…</p> : (
            <div className="space-y-4">
              <Section title={`Shared overhead · ${inrFull(r.shared_overhead)}`} rows={sep("shared_overhead_items")} tid="exp-sep-shared" />
              <Section title={`Unallocated · ${inrFull(r.unallocated)}`} rows={sep("unallocated_expense_items")} tid="exp-sep-unalloc" />
              <Section title={`Salary payments · ${inrFull(r.salary_payments)} (salary cost comes from staff records)`} rows={sep("salary_payment_items")} tid="exp-sep-salary" />
              {r.capital_expenditure > 0 && <p className="text-xs text-slate-500">Capital expenditure {inrFull(r.capital_expenditure)} is excluded from operating costs.</p>}
            </div>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
